/**
 * RemindersWidget (9.9b)
 * Shows ball-in-court items waiting on Banks (waiting_on_us)
 */
import { useBallInCourt, BallInCourtItem, BallInCourtData } from '@/hooks/useDashboardReports';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface RemindersWidgetProps {
  maxItems?: number;
}

export function RemindersWidget({ maxItems = 5 }: RemindersWidgetProps) {
  const navigate = useNavigate();
  const { data, isLoading, error } = useBallInCourt();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Reminders
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 animate-pulse">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Reminders
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Failed to load reminders</p>
        </CardContent>
      </Card>
    );
  }

  const ballInCourtData = data as BallInCourtData;
  // Filter to waiting_on_us only (Banks needs to act)
  const waitingOnUs = ballInCourtData.waiting_on_us || [];
  const items = waitingOnUs.slice(0, maxItems);

  return (
    <Card data-testid="reminders-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock className="h-5 w-5" />
            Reminders
          </CardTitle>
          {waitingOnUs.length > 0 && (
            <Badge variant="secondary">{waitingOnUs.length} items</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">No pending items</p>
        ) : (
          <div className="space-y-2">
            {items.map((item: BallInCourtItem) => (
              <div
                key={item.scope_id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/projects/${item.project_id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    navigate(`/projects/${item.project_id}`);
                  }
                }}
                className={`flex items-start gap-2 p-2 rounded text-sm cursor-pointer transition-colors ${
                  item.urgency === 'high'
                    ? 'bg-red-50 border border-red-200 hover:bg-red-100'
                    : 'bg-gray-50 hover:bg-gray-100'
                }`}
              >
                {item.urgency === 'high' && (
                  <AlertTriangle size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 truncate">
                    {item.scope_name}
                  </div>
                  <div className="text-xs text-gray-600 truncate">
                    {item.organization_name} &bull; {item.days_waiting}d waiting
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
